import { supabase } from './supabase/client';
import type {
  AiBudgetSuggestionsResult,
  SuggestBudgetsError,
  SuggestBudgetsErrorCode,
} from '@flows.cash/types';

// Sonnet weighing 6 months of category aggregates can take a while.
const CLIENT_TIMEOUT_MS = 90_000;

export type SuggestBudgetsResponse =
  | { data: AiBudgetSuggestionsResult; error: null }
  | { data: null; error: SuggestBudgetsError };

function errorResponse(code: SuggestBudgetsErrorCode): SuggestBudgetsResponse {
  return { data: null, error: { error: code } };
}

/**
 * Asks the suggest-budgets Edge Function for per-category monthly budget
 * proposals based on the user's real spending history. Nothing is created
 * server-side — callers show the suggestions in a review UI and create/update
 * budgets through the normal budget store on accept. Never throws.
 */
export async function suggestBudgets(options?: {
  months?: 3 | 6;
  locale?: string;
}): Promise<SuggestBudgetsResponse> {
  const timeout = new Promise<SuggestBudgetsResponse>((resolve) =>
    setTimeout(() => resolve(errorResponse('suggest_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<SuggestBudgetsResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('suggest-budgets', {
        body: {
          months: options?.months ?? 6,
          locale: options?.locale ?? 'en',
        },
      });

      if (error) {
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 422) return errorResponse('insufficient_data');
        return errorResponse('suggest_unavailable');
      }

      if (!Array.isArray(data?.suggestions)) {
        return errorResponse('suggest_unavailable');
      }

      return { data: data as AiBudgetSuggestionsResult, error: null };
    } catch (err) {
      console.error('suggestBudgets failed:', err);
      return errorResponse('suggest_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
